'use client';

import { clsx } from 'clsx';
import { useContext, type Ref } from 'react';
import { FieldSizeContext, Spinner } from '@auctogon/ui/web';

interface ToggleProps {
  checked: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * `id` on the switch, so a visible `<label htmlFor>` both names it and
   * toggles it on tap (BL-2111: the whole "Has title document" row is the
   * phone target, not just the 30px track).
   */
  id?: string;
  /**
   * Test-ID namespace for automated checks, rendered as `data-testid` on the
   * switch. Unset emits nothing: a settings page renders several toggles, and a
   * shared default would tag them all identically (automation-handles.md).
   */
  testId?: string;
  /**
   * Work in progress behind the switch (BL-2344: an auction downloading for offline use). The
   * knob shows the ADR 030 — Design Canon Spinner and the switch reports `aria-busy`. It stays
   * operable: turning a downloading auction off is allowed.
   */
  busy?: boolean;
  /**
   * Ref to the switch, so a form can focus it on a validation error (BL-2867:
   * React Hook Form's `field.ref`, like `NumberInput`'s `inputRef`).
   */
  buttonRef?: Ref<HTMLButtonElement>;
}

/** Pill switch — `ui-toggle` + `ui-toggle--on` per design-system.css. */
export default function Toggle({ checked, onChange, disabled, ariaLabel, id, testId, busy = false, buttonRef }: ToggleProps) {
  // BL-2293: inside a phone sheet the switch takes its touch size (48×28, 44px tap area).
  const touch = useContext(FieldSizeContext) === 'touch';
  return (
    // eslint-disable-next-line designsystem/no-raw-button -- settings-v2 dialect primitive: the ui-toggle switch, not a CTA (BL-1698)
    <button
      type="button"
      ref={buttonRef}
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={clsx('ui-toggle', touch && 'ui-toggle--touch', checked && 'ui-toggle--on', busy && 'ui-toggle--busy')}
      style={{
        background: 'none',
        border: 0,
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span className="ui-toggle__track">
        {busy && (
          <span className="ui-toggle__knob" data-testid={testId ? `${testId}-spinner` : undefined}>
            <Spinner size={10} strokeWidth={2} decorative />
          </span>
        )}
      </span>
    </button>
  );
}
